import { find as _find } from 'lodash-es';
import Link from 'next/link';
import { makeStyles } from 'tss-react/mui';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import MaterialLink from './material-link';
import ImageUpload from './image-upload';
import SocialLinks from './social-links';
import { useAppContext } from '../contexts/app';
import GroupListMembership from './group-list-membership';
import GroupListActivityInfo from './group-list-activity-info';
import type { Group } from '../types/Group';

type Props = {
  groups?: Group[];
  emptyMessage?: React.ReactNode;
  loading?: boolean;
  showMembership?: boolean;
};

const useStyles = makeStyles()((theme) => ({
  list: {
    listStyleType: 'none',
    margin: 0,
    padding: 0
  },
  listItem: {
    margin: theme.spacing(1.5, 0),
    padding: 0,
  },
  paper: {
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
    },
  },
  groupImageWrapper: {
    width: 80,
    flexShrink: 0,
    marginRight: theme.spacing(2),
    textAlign: 'center',
  },
  groupImage: {
    maxWidth: 80,
    maxHeight: 60,
  },
  content: {
    flexGrow: 1,
    overflow: 'hidden',
    minWidth: 0,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
  },
  title: {
    margin: 0,
    marginRight: theme.spacing(0.5),
  },
  lockIcon: {
    width: 12,
    height: 12,
    opacity: 0.6,
    verticalAlign: 'middle',
  },
  summary: {
    margin: theme.spacing(1.5, 0),
    '& p': {
      margin: 0,
    },
  },
  statsRow: {},
  socialRow: {},
}));

const GroupList = ({ groups, loading = false, emptyMessage = 'No groups', showMembership = true }: Props) => {
  const { classes } = useStyles();
  const { loggedInUser } = useAppContext();

  if (loading) {
    return <p>Loading...</p>;
  }

  if (!groups || !groups.length) {
    return <p>{emptyMessage}</p>;
  }

  return (
    <ul className={classes.list}>
      {groups.map((group) => {
        const membership = loggedInUser ? _find(loggedInUser.groupMembers, (gm) => gm.group.id === group.id) : null;
        return (
          <li key={group.id} className={classes.listItem}>
            <Paper className={classes.paper}>
              <div className={classes.groupImageWrapper}>
                {group.image && (
                  <Link href={`/groups/${group.slug}`} title={group.name}>
                    <ImageUpload record={group} size="80x60i" className={classes.groupImage} />
                  </Link>
                )}
              </div>
              <div className={classes.content}>
                <div className={classes.titleRow}>
                  <Typography variant="h3" className={classes.title}>
                    {group.found ? group.name : (
                      <MaterialLink href={`/groups/${group.slug}`}>
                        {group.name}
                      </MaterialLink>
                    )}
                  </Typography>
                  {showMembership && loggedInUser && (
                    <GroupListMembership group={group} membership={membership} />
                  )}
                  {!group.public && (
                    <>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src="/static/lock-solid.svg" alt="Private" className={classes.lockIcon} />
                    </>
                  )}
                </div>

                {group.summary && (
                  <Typography variant="body1" color="text.secondary" className={classes.summary} component="div">
                    {group.summary}
                  </Typography>
                )}

                <div className={classes.statsRow}>
                  <GroupListActivityInfo group={group} />
                </div>

                <div className={classes.socialRow}>
                  <SocialLinks source={group} inline compact small />
                </div>
              </div>
            </Paper>
          </li>
        );
      })}
    </ul>
  );
};

export default GroupList;
